"use client";
import React, { useState, useContext, useEffect, useRef } from "react";
import { useSignIn, useSignUp, useUser } from "@clerk/nextjs";
import { AuthContext } from "@/app/(public)/drawer";
import Button from "@/components/primitives/button";
import Form from "@/components/primitives/form";
import Info from "@/components/primitives/info";
import Icon from "@/components/primitives/icon";
import { LoadingCircle } from "@/components/primitives/loading";
import { SubscriptionPackage } from "@/utils/subscription";
import { BettorBasic } from "common";
import { usePickSlip } from "@/components/pick/create-pick";
import {
  addSubscriptionQueryParams,
  clearSubscriptionQueryParams,
  buildSubscriptionReturnUrl,
} from "@/utils/subscription-storage";

interface SegmentedCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  length: number;
  autoFocus?: boolean;
}

function SegmentedCodeInput({
  value,
  onChange,
  length,
  autoFocus,
}: SegmentedCodeInputProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [focused, setFocused] = React.useState(false);

  React.useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/\D/g, "").slice(0, length);
    onChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow backspace, delete, tab, escape, enter
    if (
      [8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)
    ) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if (
      (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
    }
  };

  return (
    <div className="relative">
      {/* Hidden input that handles the actual value */}
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        maxLength={length}
        className="absolute inset-0 opacity-0 w-full h-full cursor-default"
        style={{ caretColor: "transparent" }}
        autoComplete="one-time-code"
      />

      {/* Visual segmented display */}
      <div className="flex justify-center gap-3">
        {Array.from({ length }, (_, i) => {
          const hasValue = i < value.length;
          const isActive = focused && i === value.length;

          return (
            <div
              key={i}
              className={`
                relative w-12 h-14 border-2 rounded-lg flex items-center justify-center
                text-xl font-bold transition-all duration-200 cursor-pointer
                ${
                  isActive
                    ? "border-primary-500 ring-2 ring-primary-500/20 bg-surface-600"
                    : hasValue
                    ? "border-surface-400 bg-surface-600"
                    : "border-surface-500 bg-surface-700 hover:border-surface-400"
                }
                ${hasValue ? "text-white" : "text-secondary-400"}
              `}
              onClick={() => inputRef.current?.focus()}
            >
              {hasValue ? value[i] : ""}
              {isActive && !hasValue && (
                <div className="absolute bg-primary-500 w-0.5 h-6 animate-pulse" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface SubscriptionLoginFormProps {
  subscriptionPackage: SubscriptionPackage;
  capperProfile?: BettorBasic;
  onSuccess?: () => void;
}

type FormMode =
  | "initial"
  | "login"
  | "signup"
  | "verify-login"
  | "verify-signup";

export default function SubscriptionLoginForm({
  subscriptionPackage,
  capperProfile,
  onSuccess,
}: SubscriptionLoginFormProps) {
  const { setAuth } = useContext(AuthContext);
  const { isLoaded: signInLoaded, signIn, setActive } = useSignIn();
  const { isLoaded: signUpLoaded, signUp } = useSignUp();
  const { isLoaded: userLoaded, isSignedIn } = useUser();
  const slip = usePickSlip();

  const [mode, setMode] = useState<FormMode>("initial");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);
  const [emailDisplay, setEmailDisplay] = useState("");
  const [emailAddressId, setEmailAddressId] = useState<string | null>(null);

  const isLoaded = signInLoaded && signUpLoaded && userLoaded;

  useEffect(() => {
    if (subscriptionPackage) {
      addSubscriptionQueryParams(
        subscriptionPackage,
        slip.subscriptionCapperHandle || undefined,
        slip.subscriptionSelectedPlanId || undefined
      );
    }
  }, [
    subscriptionPackage,
    slip.subscriptionCapperHandle,
    slip.subscriptionSelectedPlanId,
  ]);

  useEffect(() => {
    if (isSignedIn && onSuccess) {
      // Don't clear query params here - let them persist for navigation
      onSuccess();
    }
  }, [isSignedIn, onSuccess]);

  const handleOAuthSignIn = (strategy: any) => {
    if (!signIn) return;

    // Build the redirect URL with subscription query params using capper handle from context
    const redirectUrl =
      subscriptionPackage && slip.subscriptionCapperHandle
        ? buildSubscriptionReturnUrl(
            window.location.href,
            subscriptionPackage,
            slip.subscriptionCapperHandle,
            slip.subscriptionSelectedPlanId || undefined
          )
        : window.location.href;

    setOauthLoading(strategy);

    signIn.authenticateWithRedirect({
      strategy,
      redirectUrl: "/express-sso-callback",
      redirectUrlComplete: redirectUrl,
    });
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !email) return;

    setLoading(true);
    setError(null);

    try {
      const signInAttempt = await signIn.create({
        identifier: email,
      });

      const emailCodeFactor = signInAttempt.supportedFirstFactors?.find(
        (factor) => factor.strategy === "email_code"
      );

      if (emailCodeFactor && emailCodeFactor.emailAddressId) {
        await signIn.prepareFirstFactor({
          strategy: "email_code",
          emailAddressId: emailCodeFactor.emailAddressId,
        });
        setEmailDisplay(emailCodeFactor.safeIdentifier || email);
        setEmailAddressId(emailCodeFactor.emailAddressId);
        setMode("verify-login");
      } else {
        await signUp.create({
          emailAddress: email,
        });

        await signUp.prepareEmailAddressVerification({
          strategy: "email_code",
        });
        setEmailDisplay(email);
        setMode("verify-signup");
      }
    } catch (err: any) {
      console.error("Email submission error:", err);

      if (
        err?.errors?.[0]?.code === "form_identifier_not_found" ||
        err?.errors?.[0]?.message?.includes("couldn't find your account") ||
        err?.errors?.[0]?.message?.includes("not found")
      ) {
        try {
          await signUp.create({
            emailAddress: email,
          });

          await signUp.prepareEmailAddressVerification({
            strategy: "email_code",
          });
          setEmailDisplay(email);
          setMode("verify-signup");
        } catch (signUpErr: any) {
          console.error("Sign up error:", signUpErr);
          setError(
            signUpErr?.errors?.[0]?.message ||
              "Something went wrong. Please try again."
          );
        }
      } else {
        setError(
          err?.errors?.[0]?.message || "Something went wrong. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !code) return;

    setLoading(true);
    setError(null);

    try {
      if (mode === "verify-login") {
        const result = await signIn.attemptFirstFactor({
          strategy: "email_code",
          code,
        });

        if (result.status === "complete") {
          await setActive({ session: result.createdSessionId });
        }
      } else if (mode === "verify-signup") {
        const result = await signUp.attemptEmailAddressVerification({
          code,
        });

        console.log("Signup verification result:", result.status, result);

        if (result.status === "complete") {
          console.log(
            "Signup complete, setting active session:",
            result.createdSessionId
          );
          await setActive({ session: result.createdSessionId });
        } else if (result.status === "missing_requirements") {
          console.log(
            "Signup has missing requirements, attempting to complete"
          );
          try {
            const completeResult = await signUp.update({});
            console.log(
              "Signup update result:",
              completeResult.status,
              completeResult
            );
            if (completeResult.status === "complete") {
              await setActive({ session: completeResult.createdSessionId });
            } else {
              setError("Account creation incomplete. Please try again.");
            }
          } catch (completeErr: any) {
            console.error("Signup completion error:", completeErr);
            setError("Failed to complete account creation. Please try again.");
          }
        } else {
          console.log("Unexpected signup status:", result.status);
          setError("Email verification failed. Please try again.");
        }
      }
    } catch (err: any) {
      console.error("Code verification error:", err);
      setError(err?.errors?.[0]?.message || "Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!isLoaded) return;

    setLoading(true);
    setError(null);

    try {
      if (mode === "verify-login" && emailAddressId) {
        await signIn.prepareFirstFactor({
          strategy: "email_code",
          emailAddressId: emailAddressId,
        });
      } else if (mode === "verify-signup") {
        await signUp.prepareEmailAddressVerification({
          strategy: "email_code",
        });
      }
      setError("Code resent successfully!");
    } catch (err: any) {
      console.error("Resend error:", err);
      setError(err?.errors?.[0]?.message || "Failed to resend code.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setMode("initial");
    setCode("");
    setError(null);
    setEmailAddressId(null);
  };

  if (!isLoaded) {
    return (
      <div className="bg-surface-700 p-6 border-surface-100 border-t">
        <div className="flex justify-center py-4">
          <LoadingCircle />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-surface-700 p-6 border-surface-100 border-t">
        <div className="flex flex-col items-center gap-4 py-4">
          <LoadingCircle />
          <div className="text-secondary-300 text-center">
            {mode === "initial" ? "Checking account..." : "Verifying code..."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-700 p-6 border-surface-100 border-t">
      {mode === "initial" && (
        <div className="space-y-4">
          <div className="mb-4 text-title-lg-bold uppercase">
            Sign in or Create Account
          </div>

          {error && <Info.Inline variant="error" message={error} filled />}

          {/* OAuth Buttons */}
          {/* <div className="space-y-3">
            <div className="gap-3 grid grid-cols-3">
              <Button
                onClick={() => handleOAuthSignIn("oauth_google")}
                disabled={loading || oauthLoading !== null}
                className="flex justify-center items-center gap-2 bg-surface-600 hover:bg-surface-500 px-4 py-3 border border-surface-400 hover:border-surface-300"
              >
                {oauthLoading === "oauth_google" ? (
                  <LoadingCircle size={16} />
                ) : (
                  <Icon name="icon-google" size="sm" />
                )}
                <span className="hidden sm:inline">Google</span>
              </Button>

              <Button
                onClick={() => handleOAuthSignIn("oauth_twitter")}
                disabled={loading || oauthLoading !== null}
                className="flex justify-center items-center gap-2 bg-surface-600 hover:bg-surface-500 px-4 py-3 border border-surface-400 hover:border-surface-300"
              >
                {oauthLoading === "oauth_twitter" ? (
                  <LoadingCircle size={16} />
                ) : (
                  <Icon name="icon-twitter" size="sm" />
                )}
                <span className="hidden sm:inline">X</span>
              </Button>

              <Button
                onClick={() => handleOAuthSignIn("oauth_discord")}
                disabled={loading || oauthLoading !== null}
                className="flex justify-center items-center gap-2 bg-surface-600 hover:bg-surface-500 px-4 py-3 border border-surface-400 hover:border-surface-300"
              >
                {oauthLoading === "oauth_discord" ? (
                  <LoadingCircle size={16} />
                ) : (
                  <Icon name="icon-discord" size="sm" />
                )}
                <span className="hidden sm:inline">Discord</span>
              </Button>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex-1 bg-surface-400 h-px"></div>
              <span className="text-secondary-400 text-sm">or</span>
              <div className="flex-1 bg-surface-400 h-px"></div>
            </div>
          </div> */}

          <Form onSubmit={handleEmailSubmit} className="space-y-4">
            <Form.Field name="email">
              <Form.Label>Email Address</Form.Label>
              <Form.Input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                disabled={oauthLoading !== null}
              />
              <Form.Message match="valueMissing">
                Please enter your email address.
              </Form.Message>
              <Form.Message match="typeMismatch">
                Please enter a valid email address.
              </Form.Message>
            </Form.Field>

            <Button
              type="submit"
              className="!justify-center w-full"
              disabled={!email || loading || oauthLoading !== null}
            >
              Continue with email address
            </Button>
          </Form>
        </div>
      )}

      {(mode === "verify-login" || mode === "verify-signup") && (
        <div className="space-y-4">
          <div className="mb-4 text-title-lg-bold uppercase">Confirm Email</div>

          <div className="mb-4 text-secondary-300 text-title-sm-medium">
            Enter the code sent to <strong>{emailDisplay}</strong>
          </div>

          {error && (
            <Info.Inline
              variant={error.includes("resent") ? "success" : "error"}
              message={error}
              filled
            />
          )}

          <Form onSubmit={handleCodeSubmit} className="space-y-4">
            <Form.Field name="code">
              <Form.Label>Verification Code</Form.Label>
              <Form.Input
                type="text"
                value={code}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d{0,6}$/.test(value)) {
                    setCode(value);
                  }
                }}
                required
                autoFocus
                maxLength={6}
                className="text-center tracking-widest"
              />
              <Form.Message match="valueMissing">
                Please enter the verification code.
              </Form.Message>
            </Form.Field>

            <Button
              type="submit"
              className="!justify-center w-full"
              disabled={code.length !== 6 || loading}
            >
              {mode === "verify-login" ? "Sign In" : "Create Account"}
            </Button>
          </Form>

          <div className="flex flex-col gap-2 text-center">
            <button
              type="button"
              onClick={handleResendCode}
              className="text-secondary-300 hover:text-secondary-200 text-sm underline"
              disabled={loading}
            >
              Resend Code
            </button>

            <button
              type="button"
              onClick={handleBackToEmail}
              className="text-secondary-300 hover:text-secondary-200 text-sm underline"
              disabled={loading}
            >
              Change Email Address
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
